import { Link } from "react-router";
import { useEffect, useState } from "react";
import InlineHighlight from "./InlineHighlight";
import { faNum, navLinks, socialLinks, TELEGRAM_GROUP } from "../lib/faData";
import { me, logout, type User } from "../lib/api";
import { currentSource } from "../lib/attribution";
import { analytics } from "../lib/analytics";

/**
 * The site chrome, ported from apps/frontend/src/pages/Fa.tsx when the Farsi
 * surface moved onto its own app.
 *
 * It is a shell rather than markup inside the landing page, because the article
 * pages need the same header and footer. On the old single-page version there
 * was nothing else to be consistent with.
 *
 * The nav links are in-page anchors, which only resolve on the home page, so
 * they are prefixed with "/" when rendered anywhere else. Without that, tapping
 * "دوره‌ها" from an article scrolled to nothing.
 */
export default function Shell({
  children,
  onHome = false,
}: {
  children: React.ReactNode;
  onHome?: boolean;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [checked, setChecked] = useState(false);

  // Resolved after hydration, never in a loader: the session cookie is scoped
  // to .gazar.dev and held by the browser, so an SSR loader would have to
  // forward it by hand and would then cache a signed-in document for everyone.
  useEffect(() => {
    let cancelled = false;
    me().then((found) => {
      if (cancelled) return;
      setUser(found);
      setChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const anchor = (href: string) => (onHome ? href : `/${href}`);

  return (
    <div className="bg-gray-50 min-h-screen">
      <header className="bg-brand-cream border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between py-5 md:py-6 lg:py-7 gap-4 md:gap-6">
            <Link to="/" className="flex items-center flex-1 min-w-0 group">
              <div className="flex flex-col min-w-0 flex-1 gap-1">
                <span
                  className="font-heading text-brand-navy text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[2.5rem] font-black uppercase leading-[1.1] truncate m-0 group-hover:opacity-80 transition-opacity duration-150 block"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  گازار
                </span>
                <p className="text-[15px] md:text-[17px] leading-[2] m-0 hidden sm:block">
                  <InlineHighlight color="navy">
                    از طراحی سیستم تا توسعه‌ی محصولات مبتنی بر هوش مصنوعی
                  </InlineHighlight>
                </p>
              </div>
            </Link>

            <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
              <div className="hidden lg:flex items-center gap-2">
                {socialLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={link.label}
                      onClick={
                        link.href === TELEGRAM_GROUP
                          ? () =>
                              analytics.telegramClick({
                                where: "chrome",
                                src: currentSource(),
                              })
                          : undefined
                      }
                      className="inline-flex h-11 w-11 items-center justify-center rounded-smooth border border-border-subtle text-brand-navy/70 transition-colors duration-150 hover:border-brand-navy hover:text-brand-navy"
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
              {/* Language switch, mirroring the فارسی link in the English nav.
                  A plain <a>, not a <Link>: gazar.dev is a different app on a
                  different origin, so client-side routing cannot reach it. */}
              <a
                href="https://gazar.dev/"
                lang="en"
                hrefLang="en"
                dir="ltr"
                className="rounded-smooth border border-border-subtle px-4 py-2.5 text-[15px] font-bold text-brand-navy transition-colors duration-150 hover:border-brand-navy"
              >
                English
              </a>
              {checked && user ? (
                <button
                  type="button"
                  onClick={async () => {
                    await logout();
                    setUser(null);
                  }}
                  className="rounded-smooth border border-border-subtle px-4 py-2.5 text-[15px] font-bold text-brand-navy transition-colors duration-150 hover:border-brand-navy"
                >
                  خروج
                </button>
              ) : (
                <Link
                  to="/vorood"
                  className="rounded-smooth border border-border-subtle px-4 py-2.5 text-[15px] font-bold text-brand-navy transition-colors duration-150 hover:border-brand-navy"
                >
                  ورود
                </Link>
              )}
            </div>
          </div>

          <nav className="flex items-center gap-6 overflow-x-auto pb-3">
            <Link
              to="/"
              className="whitespace-nowrap py-1 text-[15px] font-bold text-brand-navy transition-opacity duration-150 hover:opacity-70"
            >
              خانه
            </Link>
            <Link
              to="/maghale"
              className="whitespace-nowrap py-1 text-[15px] font-bold text-brand-navy transition-opacity duration-150 hover:opacity-70"
            >
              نوشته‌ها
            </Link>
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={anchor(l.href)}
                className="whitespace-nowrap py-1 text-[15px] text-gray-900/65 transition-colors duration-150 hover:text-brand-navy"
              >
                {l.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {children}

      <footer className="bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-9 md:py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8">
            <div>
              <h3 className="mb-3 text-[19px] font-bold uppercase text-white">گازار</h3>
              <p className="mb-3 text-[15px] leading-[2] text-white/70">
                طراحی سیستم، مهندسی نرم‌افزار و توسعه‌ی محصولات مبتنی بر هوش مصنوعی. حاصل{" "}
                {faNum(16)} سال تجربه در صنعت نرم‌افزار.
              </p>
              <p className="text-[13px] leading-[2] text-white/40">
                gazar.dev یک برند و نمونه‌کار است که توسط{" "}
                <strong className="text-white/70">QUEEK LIMITED</strong> اداره می‌شود.
              </p>
            </div>

            <div>
              <h3 className="mb-3 text-[14px] font-bold uppercase tracking-[0.18em] text-white">
                پیوندها
              </h3>
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/"
                    className="inline-flex items-center py-1 text-[15px] text-white/60 transition-colors duration-150 hover:text-white"
                  >
                    خانه
                  </Link>
                </li>
                <li>
                  <Link
                    to="/maghale"
                    className="inline-flex items-center py-1 text-[15px] text-white/60 transition-colors duration-150 hover:text-white"
                  >
                    نوشته‌ها
                  </Link>
                </li>
                {navLinks.map((l) => (
                  <li key={l.href}>
                    <a
                      href={anchor(l.href)}
                      className="inline-flex items-center py-1 text-[15px] text-white/60 transition-colors duration-150 hover:text-white"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="https://gazar.dev/"
                    className="inline-flex items-center py-1 text-[15px] text-white/60 transition-colors duration-150 hover:text-white"
                  >
                    نسخه‌ی انگلیسی سایت
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mb-3 text-[14px] font-bold uppercase tracking-[0.18em] text-white">
                ارتباط
              </h3>
              <div className="flex items-center gap-2">
                {socialLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={link.label}
                      onClick={
                        link.href === TELEGRAM_GROUP
                          ? () =>
                              analytics.telegramClick({
                                where: "chrome",
                                src: currentSource(),
                              })
                          : undefined
                      }
                      className="inline-flex h-11 w-11 items-center justify-center rounded-smooth border border-white/15 text-white/70 transition-colors duration-150 hover:border-white hover:text-white"
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-8 border-t border-white/10 pt-6">
            <p className="text-[13px] text-white/40">
              © {faNum(new Date().getFullYear())} احسان گازار
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
